"use client";
import styles from "@styles/endowment.module.css";
import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";

type FormInputs ={
  email: string;
  amount: number;
  currency: string;
  customer_name?: string;
}

const PAYSTACK_SRC = "https://js.paystack.co/v1/inline.js";

// Ensure the Paystack inline script is loaded and `PaystackPop` is available
// before we try to open the widget. Loading on demand (rather than relying on
// a page-level <Script>) removes the race where the button is clicked before
// the global exists, which made the button appear to do nothing.
function loadPaystack(): Promise<any> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined")
      return reject(new Error("Paystack can only load in the browser"));

    const w = window as any;
    if (w.PaystackPop) return resolve(w.PaystackPop);

    let script = document.querySelector<HTMLScriptElement>(
      `script[src="${PAYSTACK_SRC}"]`
    );
    if (!script) {
      script = document.createElement("script");
      script.src = PAYSTACK_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () =>
      w.PaystackPop
        ? resolve(w.PaystackPop)
        : reject(new Error("Paystack loaded but PaystackPop is undefined"))
    );
    script.addEventListener("error", () =>
      reject(new Error("Failed to load the Paystack script"))
    );
  });
}

export function EndowmentForm() {
  const [formType, setFormType] = useState<"anonymous" | "details">(
    "anonymous"
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();

  const PaystackPay: SubmitHandler<FormInputs> = async (data) => {
    try {
      const PaystackPop = await loadPaystack();
      const handler = PaystackPop.setup({
        key: process.env.NEXT_PUBLIC_PAYSTACK_KEY,
        email: data.email,
        amount: Number(data.amount) * 100,
        currency: data.currency,
        channels: ["card", "ussd"],
        metadata: {
          custom_fields: [
            {
              display_name: "Customer Name",
              variable_name: "customer_name",
              value: data.customer_name ?? "",
            },
          ],
        },
        onClose: () => console.log("Widget closed"),
        callback: () => console.log("Submitted successfully"),
      });
      handler.openIframe();
    } catch (error) {
      console.error("Could not open Paystack checkout", error);
    }
  };

  return (
    <div className={styles.endowmentHero_inner}>
      <div className={styles.form_top}>
        <button
          style={{
            backgroundColor: formType === "anonymous" ? "#8DC63F" : "#C5E19D",
            color: formType === "anonymous" ? "#ffffff" : "#000",
            fontWeight: formType === "anonymous" ? "600" : "400",
          }}
          type="button"
          onClick={() => setFormType("anonymous")}
        >
          Donate Anonymously
        </button>
        <button
          style={{
            backgroundColor: formType === "details" ? "#8DC63F" : "#C5E19D",
            color: formType === "details" ? "#ffffff" : "#000",
            fontWeight: formType === "details" ? "600" : "400",
          }}
          type="button"
          onClick={() => setFormType("details")}
        >
          Donate With Details
        </button>
      </div>
      {formType === "anonymous" && (
        <div className={styles.anonymous_form}>
          
          <form onSubmit={handleSubmit(PaystackPay)} noValidate>
            <div className={styles.form_row}>
              <div className={styles.form_options}>
                <label htmlFor="currency">Currency</label>
                <select
                  {...register("currency", {
                    required: "Please select currency",
                    validate: {
                      validCurrency: (fieldValue) => {
                        return (
                          fieldValue === "NGN" ||
                          fieldValue === "USD" ||
                          "Select currency"
                        );
                      },
                    },
                  })}
                  defaultValue={""}
                  aria-invalid={!!errors.currency}
                  aria-describedby={
                    errors.currency ? "endow_currency-error" : undefined
                  }
                  id="currency"
                >
                  <option hidden disabled value="">
                    Currency
                  </option>
                  <option className={styles.options} value="NGN">
                    NGN
                  </option>
                  <option className={styles.options} value="USD">
                    USD
                  </option>
                </select>
                {errors.currency && (
                  <p id="endow_currency-error" role="alert" className={styles.form_error}>
                    {errors.currency.message}
                  </p>
                )}
              </div>
              <div className={styles.form_input}>
                <label htmlFor="amount">amount</label>
                <input
                  {...register("amount", {
                    required: "Please enter amount.",
                    pattern: {
                      value: /^\d+$/,
                      message: "Please enter a valid amount",
                    },
                    validate: {
                      notZero: (fieldValue) => {
                        return fieldValue > 0 || "Please enter a valid amount";
                      },
                    },
                  })}
                  placeholder="Amount"
                  type="text"
                  inputMode="numeric"
                  aria-invalid={!!errors.amount}
                  aria-describedby={
                    errors.amount ? "endow_amount-error" : undefined
                  }
                  id="amount"
                />
                {errors.amount && (
                  <p id="endow_amount-error" role="alert" className={styles.form_error}>
                    {errors.amount.message}
                  </p>
                )}
              </div>
            </div>
            <div className={styles.form_row}>
              <div className={styles.form_input}>
                <label htmlFor="email">email</label>
                <input
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={
                    errors.email ? "endow_email-error" : undefined
                  }
                  id="email"
                  {...register("email", {
                    required: "Please enter email",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Please enter a valid email!",
                    },
                  })}
                />
                {errors.email && (
                  <p id="endow_email-error" role="alert" className={styles.form_error}>
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>
            <button>Pay Now</button>
          </form>
        </div>
      )}
      {formType === "details" && (
        <div className={styles.details_form}>
         
          <form onSubmit={handleSubmit(PaystackPay)} noValidate>
            <div className={styles.form_row}>
              <div className={styles.form_input}>
                <label htmlFor="email">email</label>
                <input
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  aria-describedby={
                    errors.email ? "endow_email-error" : undefined
                  }
                  id="email"
                  {...register("email", {
                    required: "Please enter email",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Please enter a valid email!",
                    },
                  })}
                />
                {errors.email && (
                  <p id="endow_email-error" role="alert" className={styles.form_error}>
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div className={styles.form_input}>
                <label htmlFor="amount">amount</label>
                <input
                  {...register("amount", {
                    required: "Please enter amount.",
                    pattern: {
                      value: /^\d+$/,
                      message: "Please enter a valid amount",
                    },
                    validate: {
                      notZero: (fieldValue) => {
                        return fieldValue > 0 || "Please enter a valid amount";
                      },
                    },
                  })}
                  placeholder="Amount"
                  type="text"
                  inputMode="numeric"
                  aria-invalid={!!errors.amount}
                  aria-describedby={
                    errors.amount ? "endow_amount-error" : undefined
                  }
                  id="amount"
                />
                {errors.amount && (
                  <p id="endow_amount-error" role="alert" className={styles.form_error}>
                    {errors.amount.message}
                  </p>
                )}
              </div>
            </div>
            <div className={styles.form_row}>
              <div className={styles.form_options}>
                <label htmlFor="currency">Currency</label>
                <select
                  {...register("currency", {
                    required: "Please select currency",
                    validate: {
                      validCurrency: (fieldValue) => {
                        return (
                          fieldValue === "NGN" ||
                          fieldValue === "USD" ||
                          "Select currency"
                        );
                      },
                    },
                  })}
                  defaultValue={""}
                  aria-invalid={!!errors.currency}
                  aria-describedby={
                    errors.currency ? "endow_currency-error" : undefined
                  }
                  id="currency"
                >
                  <option hidden disabled value="">
                    Currency
                  </option>
                  <option className={styles.options} value="NGN">
                    NGN
                  </option>
                  <option className={styles.options} value="USD">
                    USD
                  </option>
                </select>
                {errors.currency && (
                  <p id="endow_currency-error" role="alert" className={styles.form_error}>
                    {errors.currency.message}
                  </p>
                )}
              </div>
              <div className={styles.form_input}>
                <label htmlFor="name">Name</label>
                <input
                  placeholder="Name"
                  type="text"
                //   name="name"
                  id="name"
                  {...register("customer_name")}
                />
                <p className={styles.form_error}></p>
              </div>
            </div>
            <button>Pay Now</button>
          </form>
        </div>
      )}
    </div>
  );
}

